// @flow
import React from 'react';
import styled from 'styled-components';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input/Input';

import { Spoiler } from './../../../components/ui';
import Grid, { GridItem } from './../../../components/layout/Grid';
import { Selects, Card, Filter } from './../../../components';
import type { NodeFilterType, Vehicle } from '../../../lib/types';
import type { FilterChildrenParams } from '../../../components/hoc/Filter';
import { applyMaskToValue } from '../../../lib/helpers';
import { formatLicensePlateMask } from '../../../components/masked-inputs/LicensePlateInput';
import { ownerTypes } from '../../../lib/enum';
import Select from '../../../components/ui/Select';
import { DecomissedStatuses } from '../../../lib/api/tire';
import moment from 'moment';
import Datepicker from 'antd/lib/date-picker';

const { BrandsSelect, VehicleSelect } = Selects;

const { Field } = Card;

const SpoilerContent = styled.div`
  padding: 16px;
`;

const Buttons = styled(GridItem)`
  margin: 0 -10px;

  & .ant-btn {
    margin: 0 10px;
  }
`;

const { Option } = Select;

const { RangePicker } = Datepicker;

export type BatteryFilterParams = {
  code?: string,
  vehicleId?: number,
  'brand.id'?: number,
  isFree?: boolean,
  nodeId?: number,
  nodeFilterType?: NodeFilterType,
  factoryNumber?: string,
  decomissionStatus?: string,
  installDateStart: string,
  installDateEnd: string,
  removeDateStart: string,
  removeDateEnd: string,
  wasInstalledOnVehicleId? : number
};

type Props = {
  cleanFilter: Function,
  applyFilter: Function,
  filter: BatteryFilterParams
};

const BatteryFilter = ({ filter, cleanFilter, applyFilter }: Props) => (
  <Spoiler label="Фильтр">
    <SpoilerContent>
      <Grid gutter="16px">
        <Filter
          initialValues={filter}
          cleanFilter={cleanFilter}
          applyFilter={applyFilter}
        >
          {({
            values,
            changeValue,
            cleanFilter,
            applyFilter
          }: FilterChildrenParams<BatteryFilterParams>) => (
            <>
              <GridItem>
                <Field label="Марка">
                  <BrandsSelect
                    onChange={value => changeValue('brand.id', value)}
                    value={values['brand.id']}
                  />
                </Field>
              </GridItem>
              <GridItem>
                <Field label="Гос. номер">
                  <VehicleSelect
                    placeholder="Все ТС"
                    value={values.vehicleId}
                    optionTitle={(vehicle: Vehicle) =>
                      applyMaskToValue(
                        vehicle.licensePlate,
                        formatLicensePlateMask
                      )
                    }
                    filter={{
                      ownerType: ownerTypes.self
                    }}
                    onChange={value => changeValue('vehicleId', value)}
                  />
                </Field>
              </GridItem>              
              <GridItem>
                <Field label="Мнемокод">
                  <Input
                    value={values.code}
                    onChange={e => changeValue('code', e.target.value)}
                  />
                </Field>
              </GridItem>
              <GridItem>
                <Field label="Статус АКБ">
                  <Select
                    allowClear
                    placeholder="Выберите статус АКБ"
                    onChange={value => changeValue('decomissionStatus', value)}
                    value={values.decomissionStatus}
                  >
                    <Option value={DecomissedStatuses.decomissed}>
                      Списанные
                    </Option>
                    <Option value={DecomissedStatuses.notDecomissed}>
                      Несписанные
                    </Option>
                    <Option value={null}>Все</Option>
                  </Select>
                </Field>
              </GridItem>
              <GridItem>
                <Field label="Дата установки">
                  <RangePicker
                    format="DD.MM.YYYY"
                    placeholder={['Начало', 'Конец']}
                    value={[
                      values.installDateStart
                        ? moment.utc(values.installDateStart)
                        : null,
                      values.installDateEnd
                        ? moment.utc(values.installDateEnd)
                        : null
                    ]}
                    onChange={value => {
                      if (value) {
                        const [startDate, endDate] = value;
                        changeValue('installDateStart', startDate ? startDate.format() : undefined);
                        changeValue('installDateEnd', endDate ? endDate.format() : undefined);
                      }
                    }}
                  />
                </Field>
              </GridItem>
              <GridItem>
                <Field label="Гос. номер (при снятии)">
                  <VehicleSelect
                    placeholder="Все ТС"
                    value={values.wasInstalledOnVehicleId}
                    optionTitle={(vehicle: Vehicle) =>
                      applyMaskToValue(
                        vehicle.licensePlate,
                        formatLicensePlateMask
                      )
                    }
                    filter={{
                      ownerType: ownerTypes.self
                    }}
                    onChange={value => changeValue('wasInstalledOnVehicleId', value)}
                  />
                </Field>
              </GridItem>
              <GridItem>
                <Field label="Дата снятия">
                  <RangePicker
                    format="DD.MM.YYYY"
                    placeholder={['Начало', 'Конец']}
                    value={[
                      values.removeDateStart
                        ? moment.utc(values.removeDateStart)
                        : null,
                      values.removeDateEnd
                        ? moment.utc(values.removeDateEnd)
                        : null
                    ]}
                    onChange={value => {
                      if (value) {
                        const [startDate, endDate] = value;
                        changeValue('removeDateStart', startDate ? startDate.format() : undefined);
                        changeValue('removeDateEnd', endDate ? endDate.format() : undefined);
                      }
                    }}
                  />
                </Field>
              </GridItem>
              <Buttons fullWidth>
                <Button type="primary" onClick={applyFilter}>
                  Применить
                </Button>
                <Button type="plain" onClick={cleanFilter}>
                  Очистить
                </Button>
              </Buttons>
            </>
          )}
        </Filter>
      </Grid>
    </SpoilerContent>
  </Spoiler>
);

export default BatteryFilter;
